import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plane, Palmtree, Building2, MapPin, Crown, TreePine, Flag, Globe, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import InquiryForm from '../components/InquiryForm';
import { visaServices as mockVisaServices } from '../mock';
import { visaPagesData } from '../data/detailPages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Plane, Palmtree, Building2, MapPin, Crown, TreePine, Flag, Globe
};

const VisaServices = () => {
  const [visaList, setVisaList] = useState(mockVisaServices);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchVisas = async () => {
      try {
        const res = await axios.get(`${API}/visa-services`);
        if (res.data && res.data.length > 0) setVisaList(res.data);
      } catch (e) {
        // Use mock data as fallback
      }
    };
    fetchVisas();
  }, []);

  const handleApply = (visa) => {
    setSelectedVisa(visa);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        inquiry_type: 'visa',
        subject: `Visa Inquiry: ${selectedVisa?.country}`,
        details: { visa_country: selectedVisa?.country, visa_type: selectedVisa?.type }
      });
      toast.success('Inquiry submitted! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setDialogOpen(false);
    } catch (err) {
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Visa Services - Dubai, Schengen, Thailand, UK, Canada from Surat"
        description="Expert visa consultancy in Surat by Varni Visa. Apply for Dubai, Thailand, Singapore, Schengen, UK, Canada, USA, Australia tourist visas. Fast processing, high approval rates."
        keywords="Visa Consultant in Surat, Dubai Visa, Schengen Visa Consultant, Thailand Visa, Singapore Visa, UK Visa, Canada Visa"
        path="/visa-services"
      />
      <section 
        className="relative h-[350px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(45, 141, 180, 0.85), rgba(247, 148, 93, 0.75)), url('https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHx2aXNhJTIwcGFzc3BvcnR8ZW58MHx8fHwxNzgwMjM3NDg2fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Visa Services</h1>
          <p className="text-xl">Expert visa assistance for destinations worldwide</p>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Consultation', desc: 'Free initial consultation about your visa needs' },
              { step: '2', title: 'Documentation', desc: 'We help prepare all required documents' },
              { step: '3', title: 'Application', desc: 'Submit application with expert review' },
              { step: '4', title: 'Approval', desc: 'Get your visa approved hassle-free' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visa List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Choose Your Destination</h2>
            <p className="text-gray-600">Get expert guidance for your visa application</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visaList.map((visa) => {
              const Icon = iconMap[visa.icon] || Globe;
              const slug = visa.country.toLowerCase();
              const hasDetailPage = !!visaPagesData[slug];
              return (
                <Card key={visa.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded-lg flex items-center justify-center mb-3">
                      <Icon className="text-white" size={24} />
                    </div>
                    <CardTitle>{visa.country} Visa</CardTitle>
                    <CardDescription>{visa.type}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-[#2D8DB4]" />
                        <span>{visa.processingTime}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-[#2D8DB4]" />
                        <span>Validity: {visa.validity}</span>
                      </div>
                      <p className="text-2xl font-bold text-[#F7945D] pt-3">{visa.price}</p>
                    </div>
                  </CardContent>
                  <CardFooter className="gap-2">
                    {hasDetailPage ? (
                      <Link to={`/visa/${slug}`} className="flex-1">
                        <Button className="w-full bg-[#2D8DB4] hover:bg-[#247a9a]">
                          View Details <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        className="w-full bg-[#2D8DB4] hover:bg-[#247a9a]"
                        onClick={() => handleApply(visa)}
                      >
                        Apply Now
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom inquiry */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-2">Need Help Choosing the Right Visa?</h2>
          <p className="text-center text-gray-600 mb-8">Talk to our visa expert — free, no obligation</p>
          <InquiryForm inquiryType="visa" defaultSubject="Visa Consultation Request" />
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for {selectedVisa?.country} Visa</DialogTitle>
            <DialogDescription>
              Fill in your details and our experts will contact you shortly
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any specific requirements?"
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#2D8DB4] hover:bg-[#247a9a]" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisaServices;
